import { useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import { PermissionHint } from "@/components/admin/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi, apiErrorMessage } from "@/lib/api";
import { can, requiredRole } from "@/lib/permissions";
import { queryKeys, useCurrentIdentityQuery } from "@/lib/queries";

const productKeySlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function CreateProductPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const identityQuery = useCurrentIdentityQuery();
  const allowed = can(identityQuery.data?.roles, "createRecords");
  const [error, setError] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef<HTMLInputElement>(null);
  const mutation = useMutation({
    mutationFn: adminApi.createProduct,
    onSuccess: async (product) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products });
      navigate(`/admin/products?product=${encodeURIComponent(product.id)}`);
    },
    onError: (value) => setError(apiErrorMessage(value)),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) return;
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") ?? "").trim();
    const key = String(form.get("key") ?? "");
    const reason = String(form.get("reason") ?? "").trim();
    if (!name || !key || !reason) {
      setKeyError(null);
      setError("Name, product key, and reason are required.");
      const firstInvalid = !name
        ? nameRef.current
        : !key
          ? event.currentTarget.elements.namedItem("key")
          : event.currentTarget.elements.namedItem("reason");
      (firstInvalid as HTMLElement | null)?.focus();
      return;
    }
    if (!productKeySlugPattern.test(key)) {
      setError(null);
      setKeyError(t("products.form.keyFormat", "Use lowercase a-z, 0-9, and single hyphens between groups."));
      keyRef.current?.focus();
      return;
    }
    setKeyError(null);
    setError(null);
    mutation.mutate({
      name,
      key,
      platforms: String(form.get("platforms") ?? "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      reason,
    });
  }

  return (
    <PageFrame size="form">
      <PageHeader
        title={
          <>
            <Package className="size-6 text-muted sm:size-7" aria-hidden="true" />
            {t("products.form.createTitle", "Create product")}
          </>
        }
        titleClassName="flex items-center gap-2.5"
        description={t("products.form.createDesc", "Define the Product name, key slug, supported platforms, and an audited reason.")}
        actions={
          <Button variant="ghost" onClick={() => navigate("/admin/products")}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("common.back", "Back to products")}
          </Button>
        }
      />
      {!allowed && (
        <PermissionHint
          role={requiredRole("createRecords")}
          action="create products"
        />
      )}
      <form
        className="rounded-lg border border-line bg-panel p-5 sm:p-7"
        onSubmit={submit}
        noValidate
      >
        <fieldset disabled={!allowed} className="min-w-0 space-y-6">
          <section className="space-y-4">
            <h2 className="font-semibold text-ink">{t("products.form.identity", "Product identity")}</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="product-name">{t("products.form.name", "Product name")}</Label>
                <Input
                  ref={nameRef}
                  id="product-name"
                  name="name"
                  required
                  aria-invalid={Boolean(error)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product-key">{t("products.form.key", "Product key")}</Label>
                <Input
                  ref={keyRef}
                  id="product-key"
                  name="key"
                  required
                  aria-invalid={Boolean(keyError)}
                  aria-describedby={keyError ? "product-key-error" : undefined}
                  onChange={() => setKeyError(null)}
                  placeholder="desktop-pro"
                />
                {keyError && <p id="product-key-error" className="text-sm text-danger" role="alert">{keyError}</p>}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="product-platforms">{t("products.form.platforms", "Platforms")}</Label>
                <Input
                  id="product-platforms"
                  name="platforms"
                  defaultValue="windows, darwin"
                />
              </div>
            </div>
          </section>
          <section className="space-y-2">
            <Label htmlFor="product-reason">{t("dialogs.reason", "Reason")}</Label>
            <Input
              id="product-reason"
              name="reason"
              required
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "product-form-error" : undefined}
              placeholder="Approved product registration"
            />
          </section>
          {error && (
            <div id="product-form-error">
              <InlineAlert title="Product not created" message={error} />
            </div>
          )}
        </fieldset>
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/products")}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!allowed || mutation.isPending}
          >
            {mutation.isPending ? (
              <BusyLabel>Creating</BusyLabel>
            ) : (
              t("products.form.saveProduct", "Create product")
            )}
          </Button>
        </div>
      </form>
    </PageFrame>
  );
}
